/**
 * @cfleet/auth - usePermissions hook
 *
 * Hook for checking user permissions in components.
 * Provides convenient methods for permission-based UI rendering.
 */

import { useMemo, useCallback } from 'react';
import { useAuth } from '../AuthProvider';
import type { UserPermissions, UserRole } from '../types';

/**
 * Return type for usePermissions hook
 */
export interface UsePermissionsResult {
  /** All current user permissions */
  permissions: UserPermissions | null;
  /** Current user role */
  role: UserRole | null;
  /** Check if user has a specific permission */
  hasPermission: (permission: keyof UserPermissions) => boolean;
  /** Check if user has all specified permissions */
  hasAllPermissions: (permissions: (keyof UserPermissions)[]) => boolean;
  /** Check if user has any of the specified permissions */
  hasAnyPermission: (permissions: (keyof UserPermissions)[]) => boolean;
  /** Check if user has a specific role */
  hasRole: (role: UserRole) => boolean;
  /** Check if user has any of the specified roles */
  hasAnyRole: (roles: UserRole[]) => boolean;
  /** Check if user is an admin */
  isAdmin: boolean;
  /** Check if user is a dispatcher or higher */
  isDispatcherOrAbove: boolean;
  /** Check if user is a manager or higher */
  isManagerOrAbove: boolean;
  /** Check if user can view data (has any view permission) */
  canView: boolean;
  /** Check if user can manage data (has any manage permission) */
  canManage: boolean;
}

/**
 * Role hierarchy for permission checks
 * Higher index = more permissions
 */
const ROLE_HIERARCHY: UserRole[] = ['Customer', 'Driver', 'Dispatcher', 'Manager', 'Admin'];

/**
 * Hook for checking user permissions
 *
 * @example
 * ```tsx
 * function AdminPanel() {
 *   const { hasPermission, isAdmin } = usePermissions();
 *
 *   if (!isAdmin) {
 *     return <AccessDenied />;
 *   }
 *
 *   return (
 *     <div>
 *       {hasPermission('manageDrivers') && <DriverManagement />}
 *       {hasPermission('viewAudit') && <AuditLogs />}
 *     </div>
 *   );
 * }
 * ```
 */
export function usePermissions(): UsePermissionsResult {
  const { user } = useAuth();

  const permissions = useMemo(() => user?.permissions ?? null, [user]);
  const role = useMemo(() => user?.role ?? null, [user]);

  /**
   * Check if user has a specific permission
   */
  const hasPermission = useCallback(
    (permission: keyof UserPermissions): boolean => {
      if (!permissions) return false;
      return permissions[permission] === true;
    },
    [permissions]
  );

  /**
   * Check if user has all specified permissions
   */
  const hasAllPermissions = useCallback(
    (requiredPermissions: (keyof UserPermissions)[]): boolean => {
      if (!permissions) return false;
      return requiredPermissions.every((p) => permissions[p] === true);
    },
    [permissions]
  );

  /**
   * Check if user has any of the specified permissions
   */
  const hasAnyPermission = useCallback(
    (requiredPermissions: (keyof UserPermissions)[]): boolean => {
      if (!permissions) return false;
      return requiredPermissions.some((p) => permissions[p] === true);
    },
    [permissions]
  );

  /**
   * Check if user has a specific role
   */
  const hasRole = useCallback(
    (requiredRole: UserRole): boolean => {
      return role === requiredRole;
    },
    [role]
  );

  /**
   * Check if user has any of the specified roles
   */
  const hasAnyRole = useCallback(
    (roles: UserRole[]): boolean => {
      if (!role) return false;
      return roles.includes(role);
    },
    [role]
  );

  /**
   * Check if user is an admin
   */
  const isAdmin = useMemo(() => role === 'Admin', [role]);

  /**
   * Check if user is a dispatcher or higher in the role hierarchy
   */
  const isDispatcherOrAbove = useMemo(() => {
    if (!role) return false;
    const roleIndex = ROLE_HIERARCHY.indexOf(role);
    const dispatcherIndex = ROLE_HIERARCHY.indexOf('Dispatcher');
    return roleIndex >= dispatcherIndex;
  }, [role]);

  /**
   * Check if user is a manager or higher in the role hierarchy
   */
  const isManagerOrAbove = useMemo(() => {
    if (!role) return false;
    const roleIndex = ROLE_HIERARCHY.indexOf(role);
    const managerIndex = ROLE_HIERARCHY.indexOf('Manager');
    return roleIndex >= managerIndex;
  }, [role]);

  /**
   * Check if user has any view permission
   */
  const canView = useMemo(() => {
    if (!permissions) return false;
    return permissions.viewClients || permissions.viewAudit || permissions.viewAnalytics;
  }, [permissions]);

  /**
   * Check if user has any manage permission
   */
  const canManage = useMemo(() => {
    if (!permissions) return false;
    return permissions.manageDrivers || permissions.manageRoutes || permissions.manageOrders;
  }, [permissions]);

  return {
    permissions,
    role,
    hasPermission,
    hasAllPermissions,
    hasAnyPermission,
    hasRole,
    hasAnyRole,
    isAdmin,
    isDispatcherOrAbove,
    isManagerOrAbove,
    canView,
    canManage,
  };
}
